/**
 * Zustand store slice — admin/manage tab state.
 *
 * Tracks the list of checklists (with their rules) and which checklist
 * is currently selected for rule management.
 */

import { create } from 'zustand';
import type { ChecklistDetailOut, RuleOut } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AdminState {
  checklists: ChecklistDetailOut[];
  selectedChecklistId: string | null;
  isLoading: boolean;
  globalError: string | null;

  setChecklists: (checklists: ChecklistDetailOut[]) => void;
  addChecklist: (checklist: ChecklistDetailOut) => void;
  removeChecklist: (id: string) => void;
  setSelectedChecklistId: (id: string | null) => void;
  addRuleToSelectedChecklist: (rule: RuleOut) => void;
  removeRuleFromSelectedChecklist: (ruleId: string) => void;
  setIsLoading: (loading: boolean) => void;
  setGlobalError: (error: string | null) => void;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAdminStore = create<AdminState>((set) => ({
  checklists: [],
  selectedChecklistId: null,
  isLoading: false,
  globalError: null,

  setChecklists: (checklists) => set({ checklists }),
  addChecklist: (checklist) =>
    set((state) => ({ checklists: [checklist, ...state.checklists] })),

  removeChecklist: (id) =>
    set((state) => ({
      checklists: state.checklists.filter((cl) => cl.id !== id),
      selectedChecklistId:
        state.selectedChecklistId === id ? null : state.selectedChecklistId,
    })),

  setSelectedChecklistId: (id) =>
    set((state) => ({
      selectedChecklistId: state.selectedChecklistId === id ? null : id,
    })),

  addRuleToSelectedChecklist: (rule) =>
    set((state) => ({
      checklists: state.checklists.map((cl) =>
        cl.id === state.selectedChecklistId
          ? { ...cl, rules: [...cl.rules, rule] }
          : cl,
      ),
    })),

  removeRuleFromSelectedChecklist: (ruleId) =>
    set((state) => ({
      checklists: state.checklists.map((cl) =>
        cl.id === state.selectedChecklistId
          ? { ...cl, rules: cl.rules.filter((r) => r.id !== ruleId) }
          : cl,
      ),
    })),

  setIsLoading: (loading) => set({ isLoading: loading }),
  setGlobalError: (error) => set({ globalError: error }),

  reset: () =>
    set({
      checklists: [],
      selectedChecklistId: null,
      isLoading: false,
      globalError: null,
    }),
}));
